import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Stack, Chip, LinearProgress, Alert, CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Payment, Upload, Phone, NoteAdd, DateRange } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { doAddCollectionNote } from '../../../reduxcomponents/slices/tripManagementSlice';
import PaymentScheduleModal from '../../query/PaymentScheduleModal';

const CollectionFollowupDialog = ({ open, trip, onClose }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(s => s.tripManagement);
  const [form, setForm] = useState({ note: '', amount: '', proof_url: '' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const pct = trip?.payment?.total_amount
    ? Math.round(((trip.payment.paid_amount || 0) / trip.payment.total_amount) * 100)
    : 0;

  const handleChange = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.note && !form.amount) { setError('Add a note or amount.'); return; }
    setError('');
    try {
      const collected = form.amount ? parseFloat(form.amount) : 0;
      await dispatch(doAddCollectionNote({
        id: trip._id,
        operationId: trip.operation_id,
        data: { collected, note: form.note, proof_url: form.proof_url || undefined },
      })).unwrap();
      setSuccess('Collection note saved!');
      setForm({ note: '', amount: '', proof_url: '' });
      setTimeout(() => { setSuccess(''); onClose(); }, 1500);
    } catch (err) {
      setError(err || 'Failed to save.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <Payment color="primary" />
          <Box>
            <Typography variant="h6">Collection Follow-up</Typography>
            <Typography variant="caption" color="text.secondary">{trip?.guest_id?.name}</Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {/* Payment Summary */}
        <Box bgcolor="grey.50" p={2} borderRadius={1.5} mb={2}>
          <Stack direction="row" spacing={3} mb={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h6" fontWeight={700}>
                ₹{(trip?.payment?.total_amount || 0).toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Paid</Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                ₹{(trip?.payment?.paid_amount || 0).toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Due</Typography>
              <Typography variant="h6" fontWeight={700} color="error.main">
                ₹{(trip?.payment?.due_amount || 0).toLocaleString()}
              </Typography>
            </Box>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={pct === 100 ? 'success' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {pct}% collected
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Stack direction="row" spacing={1} mb={2}>
          {trip?.guest_id?.phone && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Phone />}
              href={`tel:${trip.guest_id.country_code || ''}${trip.guest_id.phone}`}
            >
              Call Guest
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DateRange />}
            onClick={() => setScheduleModalOpen(true)}
          >
            Payment Schedule
          </Button>
        </Stack>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

        {/* Collection Form */}
        <Stack spacing={2}>
          <TextField
            label="Collection Note"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={form.note}
            onChange={handleChange('note')}
            placeholder="e.g. Called guest, promised to pay by Friday..."
            InputProps={{ startAdornment: <InputAdornment position="start"><NoteAdd fontSize="small" /></InputAdornment> }}
          />
          <TextField
            label="Amount Received (₹)"
            type="number"
            fullWidth
            size="small"
            value={form.amount}
            onChange={handleChange('amount')}
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <TextField
            label="Payment Proof URL"
            fullWidth
            size="small"
            value={form.proof_url}
            onChange={handleChange('proof_url')}
            placeholder="https://..."
            InputProps={{ startAdornment: <InputAdornment position="start"><Upload fontSize="small" /></InputAdornment> }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading.action}
          startIcon={loading.action ? <CircularProgress size={16} /> : <Payment />}
        >
          Save Collection
        </Button>
      </DialogActions>

      {trip && (
        <PaymentScheduleModal
          open={scheduleModalOpen}
          queryData={trip._raw}
          onClose={() => setScheduleModalOpen(false)}
        />
      )}
    </Dialog>
  );
};

export default CollectionFollowupDialog;
